import { z } from 'zod';

import { ROLE_RANK, type WorkspaceRole } from './enums.js';

/**
 * GELİŞTİRİCİ API ANAHTARLARI — 18 Eyl 2026.
 *
 * Dış istemcilerin (REST, `api.mentorivo.com/mcp` sunucusu, yapay zekâ
 * ajanları) kimliği. Tarayıcı oturumunun YERİNE geçmiyor; ikisi yan yana
 * yaşıyor ve guard hangisinin geldiğine başlıktaki önekten karar veriyor.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 Anahtarın yetkisi İKİ tavanın küçüğü
 * ═══════════════════════════════════════════════════════════════════════════
 *   etkin rol = min(kapsamın tavanı, oluşturanın O ANKİ rolü)
 *
 * Oluşturanın rolü her istekte DB'den okunuyor (`WorkspaceContextGuard`). Rolü
 * düşürülen ya da çalışma alanından çıkarılan bir üyenin anahtarı AYNI ANDA
 * güç kaybediyor — "üyeyi çıkardım ama anahtarı unuttum" diye bir açık yok.
 *
 * Kapsamın tavanı OWNER'a hiç çıkmıyor: fatura, sahiplik devri ve çalışma
 * alanı silme bir anahtarla YAPILAMAZ. Zaten o uçlar anahtara kapalı
 * (`@AllowApiKey` taşımıyorlar) — tavan ikinci bir kilit.
 */

/// Ham anahtarın öneki. Guard `Authorization: Bearer mvk_…` görünce oturum
/// jetonu değil anahtar yolunu izliyor. Gizli tarayıcılar (GitHub secret
/// scanning vb.) sızıntıyı bu önekle tanıyabilsin diye sabit ve ayırt edici.
export const API_KEY_PREFIX = 'mvk_live_';

/// Önekten sonraki rastgele kısım: 32 karakter base62 ≈ 190 bit.
export const API_KEY_BODY_LENGTH = 32;

/// Tam anahtarın biçimi — guard biçimsiz girdiyi DB'ye gitmeden reddediyor.
export const API_KEY_PATTERN = /^mvk_live_[A-Za-z0-9]{32}$/;

export const API_KEY_SCOPES = ['READ', 'WRITE'] as const;
export type ApiKeyScope = (typeof API_KEY_SCOPES)[number];

/// Kapsamın izin verdiği en yüksek rol.
export const API_KEY_SCOPE_ROLE_CAP: Record<ApiKeyScope, WorkspaceRole> = {
  READ: 'VIEWER',
  WRITE: 'ADMIN',
};

/// Çalışma alanı başına etkin (iptal edilmemiş) anahtar tavanı.
export const API_KEY_MAX_ACTIVE_PER_WORKSPACE = 25;

/// Anahtarın bu istekteki etkin rolü.
export function apiKeyEffectiveRole(scope: ApiKeyScope, memberRole: WorkspaceRole): WorkspaceRole {
  const cap = API_KEY_SCOPE_ROLE_CAP[scope];
  return ROLE_RANK[memberRole] < ROLE_RANK[cap] ? memberRole : cap;
}

export const apiKeyCreateSchema = z.object({
  name: z.string().trim().min(1).max(80),
  scope: z.enum(API_KEY_SCOPES).default('READ'),
  /// Gün cinsinden ömür. Verilmezse süresiz — kullanıcı arayüzde açıkça seçer.
  expiresInDays: z.number().int().min(1).max(730).optional(),
});
export type ApiKeyCreateInput = z.infer<typeof apiKeyCreateSchema>;

/// Liste satırı. Ham anahtar ve özeti HİÇBİR yanıtta yok.
export interface ApiKeyRow {
  id: string;
  name: string;
  scope: ApiKeyScope;
  /// `mvk_live_AbCd…WxYz` — tanımak için, kullanmak için değil.
  maskedKey: string;
  createdAt: string;
  createdBy: { id: string; name: string | null; email: string };
  lastUsedAt: string | null;
  lastUsedIp: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  /// Süresi dolmuş ya da iptal edilmişse false.
  active: boolean;
}

/// Oluşturma yanıtı — ham anahtar YALNIZCA burada, bir kez.
export interface ApiKeyCreated {
  key: string;
  apiKey: ApiKeyRow;
}

export const ApiKeyErrorCode = {
  /// Anahtar biçimsiz, bilinmiyor, iptal edilmiş ya da süresi dolmuş. HTTP 401.
  /// Dört durum aynı yanıtı veriyor: hangisi olduğunu söylemek, çalınmış
  /// bir listenin hangi satırlarının hâlâ canlı olduğunu tahmin ettirir.
  API_KEY_INVALID: 'API_KEY_INVALID',
  /// Anahtar geçerli ama bu uç anahtara açık değil (oturum gerektiriyor). HTTP 403.
  API_KEY_NOT_ALLOWED: 'API_KEY_NOT_ALLOWED',
  /// Uç veri değiştiriyor ve WRITE kapsam istiyor; anahtar READ. HTTP 403.
  /// Rol kontrolünden AYRI ve ondan önce: READ anahtarın etkin rolü zaten
  /// VIEWER ama bazı yazma uçları (ajan önerisine karar) yetenek olarak
  /// VIEWER'a açık ve asıl kontrolü serviste yapıyor. Kapsam kapısı, READ
  /// anahtarın hiçbir yazma ucuna dokunamayacağını tek satırda garanti ediyor.
  API_KEY_SCOPE_INSUFFICIENT: 'API_KEY_SCOPE_INSUFFICIENT',
  /// Çalışma alanındaki etkin anahtar tavanı dolu. HTTP 409.
  API_KEY_LIMIT_REACHED: 'API_KEY_LIMIT_REACHED',
} as const;
export type ApiKeyErrorCode = (typeof ApiKeyErrorCode)[keyof typeof ApiKeyErrorCode];

// ═══════════════════════════════════════════════════════════════════════════
// IP kısıtı — çalışma alanı geneli
// ═══════════════════════════════════════════════════════════════════════════
//
// `Workspace.apiKeyAllowedIps` doluysa o çalışma alanının BÜTÜN anahtarları
// yalnız bu adreslerden kabul edilir. Liste boşsa kısıt yok.
//
// 🔴 Listeye takılan istek, GEÇERSİZ ANAHTARLA AYNI yanıtı alıyor (401
// `API_KEY_INVALID`) — kullanıcı kararı. Ayrı bir kod dönseydi, çalınmış bir
// anahtarı deneyen biri "anahtar doğru ama yerin yanlış" bilgisini alırdı;
// yani listenin varlığını ve anahtarın geçerliliğini birden öğrenirdi.
// Sebep sunucu günlüğüne yazılıyor: destek tarafı kör kalmıyor.

/// Listeye yazılabilecek en fazla kural. Sınır keyfi değil: her istekte
/// baştan sona taranıyor ve uzun liste, yönetilemeyen liste demek.
export const API_KEY_IP_RULE_MAX = 30;

const IPV4 = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;

/// IPv4 → 32 bitlik sayı. Geçersizse `null`.
function parseIpv4(value: string): bigint | null {
  const match = IPV4.exec(value);
  if (!match) return null;
  let out = 0n;
  for (let i = 1; i <= 4; i++) {
    const part = Number(match[i]);
    // "010" gibi baştan sıfırlı yazımlar reddediliyor: bazı kütüphaneler
    // onları sekizlik sayar ve aynı metin iki farklı adres olur.
    if (!Number.isInteger(part) || part < 0 || part > 255) return null;
    if (match[i]!.length > 1 && match[i]!.startsWith('0')) return null;
    out = (out << 8n) | BigInt(part);
  }
  return out;
}

/// IPv6 → 128 bitlik sayı. `::` kısaltmasını ve gömülü IPv4'ü destekler.
function parseIpv6(value: string): bigint | null {
  const text = value.split('%')[0]!; // bölge kimliği (fe80::1%eth0) atılıyor
  if (!text.includes(':')) return null;

  const halves = text.split('::');
  if (halves.length > 2) return null;

  const toWords = (part: string): bigint[] | null => {
    if (part === '') return [];
    const words: bigint[] = [];
    const pieces = part.split(':');
    for (const [index, piece] of pieces.entries()) {
      // Son parça IPv4 olabilir: ::ffff:192.0.2.1
      if (index === pieces.length - 1 && piece.includes('.')) {
        const v4 = parseIpv4(piece);
        if (v4 === null) return null;
        words.push((v4 >> 16n) & 0xffffn, v4 & 0xffffn);
        continue;
      }
      if (!/^[0-9a-fA-F]{1,4}$/.test(piece)) return null;
      words.push(BigInt(parseInt(piece, 16)));
    }
    return words;
  };

  const head = toWords(halves[0]!);
  const tail = halves.length === 2 ? toWords(halves[1]!) : [];
  if (head === null || tail === null) return null;

  const missing = 8 - head.length - tail.length;
  if (halves.length === 2 ? missing < 0 : missing !== 0) return null;

  const words = [...head, ...Array<bigint>(halves.length === 2 ? missing : 0).fill(0n), ...tail];
  return words.reduce((acc, word) => (acc << 16n) | word, 0n);
}

interface ParsedAddress {
  value: bigint;
  bits: 32 | 128;
}

/// IPv4-mapped IPv6 (`::ffff:1.2.3.4`) IPv4'e indiriliyor: nginx'in ilettiği
/// adres bazı kurulumlarda bu biçimde geliyor ve `1.2.3.4` kuralıyla
/// eşleşmemesi kullanıcının anlayamayacağı bir ret olurdu.
/// `::ffff:0:0/96` bloğunun üst 96 biti. (Sabit bir dönem `0xffff << 32`
/// yazılmıştı; 96 bit sağa kaydırılmış değerle karşılaştırıldığı için hiç
/// eşleşmiyordu ve gömülü IPv4 adresleri kısıta takılıyordu — testle yakalandı.)
const V4_MAPPED_PREFIX = 0xffffn;

export function parseIpAddress(value: string): ParsedAddress | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;

  const v4 = parseIpv4(trimmed);
  if (v4 !== null) return { value: v4, bits: 32 };

  const v6 = parseIpv6(trimmed);
  if (v6 === null) return null;
  if (v6 >> 32n === V4_MAPPED_PREFIX) return { value: v6 & 0xffffffffn, bits: 32 };
  return { value: v6, bits: 128 };
}

/// Tek IP ya da CIDR (`203.0.113.7`, `10.0.0.0/8`, `2001:db8::/32`).
export function isIpRule(value: string): boolean {
  const [address, prefix, ...rest] = value.trim().split('/');
  if (rest.length > 0 || address === undefined) return false;

  const parsed = parseIpAddress(address);
  if (!parsed) return false;
  if (prefix === undefined) return true;

  if (!/^\d{1,3}$/.test(prefix)) return false;
  const length = Number(prefix);
  return length >= 0 && length <= parsed.bits;
}

/**
 * İstek bu listeye göre kabul edilir mi.
 *
 * Boş liste = kısıt yok. Liste doluyken adres bilinmiyorsa (`null`) istek
 * REDDEDİLİYOR: kısıt koyan bir çalışma alanında "adresi okuyamadım" durumu
 * geçiş sebebi olamaz.
 */
export function isIpAllowed(ip: string | null, rules: readonly string[]): boolean {
  if (rules.length === 0) return true;
  if (!ip) return false;

  const address = parseIpAddress(ip);
  if (!address) return false;

  return rules.some((rule) => {
    const [text, prefix] = rule.trim().split('/');
    const target = text === undefined ? null : parseIpAddress(text);
    if (!target || target.bits !== address.bits) return false;

    const length = prefix === undefined ? address.bits : Number(prefix);
    if (!Number.isInteger(length) || length < 0 || length > address.bits) return false;

    const shift = BigInt(address.bits - length);
    return address.value >> shift === target.value >> shift;
  });
}

export const ipRuleSchema = z
  .string()
  .trim()
  .max(49)
  .refine(isIpRule, 'IP adresi ya da CIDR bloğu olmalı (ör. 203.0.113.7 veya 10.0.0.0/24)');

export const apiKeyAllowedIpsSchema = z.array(ipRuleSchema).max(API_KEY_IP_RULE_MAX);

/// Panelden gelen çok satırlı metni listeye çevirir (virgül ve satır sonu).
export function parseIpRuleList(text: string): string[] {
  return text
    .split(/[\n,;]+/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}
